import {
  LabwareItemInput,
  LocationInput,
  LocationRangeInput,
  SlotFillingRule,
  SlotRangeInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import range from 'lodash/range';

import { ParsedSlot } from '../../types';

/** Parses a raw slot range string to ParsedSlot[], or returns null if the string is malformed.  */
export const parseSlotString = (slots: string): ParsedSlot[] | null => {
  const result: ParsedSlot[] = [];

  // trim whitespace, remove single trailing comma
  const trimmed = slots.trim().replace(/,$/, '');

  const whitespaceBetweenDigits = /\d\s\d/g;

  if (whitespaceBetweenDigits.test(trimmed)) {
    // has internal whitespace, eg '1 2'
    return null;
  }

  const groups = trimmed.split(',');
  for (const group of groups) {
    const startAndEnd = group.split('-');

    switch (startAndEnd.length) {
      case 1: {
        // eg '1'
        const slot = Number.parseInt(group);

        if (Number.isNaN(slot) || slot < 1) return null;

        result.push(slot);
        break;
      }
      case 2: {
        // eg '5-10'
        const start = Number.parseInt(startAndEnd[0] ?? '');
        const end = Number.parseInt(startAndEnd[1] ?? '');

        if (
          Number.isNaN(start) ||
          Number.isNaN(end) ||
          start < 1 ||
          end < 1 ||
          start > end
        )
          return null;

        result.push({ start, end });
        break;
      }
      default: {
        // neither 1 nor 2: this must be a malformed slot range string
        return null;
      }
    }
  }
  return result;
};

export const stringifySlots = (slots: number[]): string | null => {
  if (!slots.length) return null;

  const sortedSlots = [...slots].sort((a, b) => a - b);

  const parts: string[] = [];

  // Get the first element safely
  const firstSlot = sortedSlots[0];
  // With noUncheckedIndexedAccess, we need to check even though we know it exists
  if (firstSlot === undefined) return null;

  let currentStart = firstSlot;
  let currentEnd = firstSlot;

  // Process each slot
  for (let i = 1; i < sortedSlots.length; i++) {
    const currentSlot = sortedSlots[i];
    // Skip undefined values (shouldn't happen with normal arrays)
    if (currentSlot === undefined) continue;

    if (currentSlot === currentEnd + 1) {
      currentEnd = currentSlot;
    } else {
      parts.push(formatRange(currentStart, currentEnd));
      currentStart = currentSlot;
      currentEnd = currentSlot;
    }
  }

  parts.push(formatRange(currentStart, currentEnd));

  return parts.join(',');
};

/** Converts ParsedSlot[] back to a slot range string - inverse of parseSlotString */
export const stringifyParsedSlots = (
  parsedSlots: ParsedSlot[]
): string | null => {
  if (!parsedSlots.length) return null;

  const parts: string[] = [];

  for (const slot of parsedSlots) {
    if (typeof slot === 'number') {
      parts.push(slot.toString());
    } else {
      parts.push(formatRange(slot.start, slot.end));
    }
  }

  return parts.join(',');
};

const formatRange = (start: number, end: number): string => {
  return start === end ? `${start}` : `${start}-${end}`;
};

/** Breaks apart slot ranges into individual slots.
 * Intentionally permits duplicates (slot collisions). */
export const parseSlotsToIndividualNumbers = (
  parsedSlots: ParsedSlot[]
): number[] => {
  return parsedSlots.flatMap((slots) => {
    if (typeof slots === 'number') return slots;
    return range(slots.start, slots.end + 1);
  });
};

/** Convert VB's ParsedSlot to API type SlotRangeInput */
export const parsedSlotToSlotRangeInput = (
  slot: ParsedSlot
): SlotRangeInput => {
  if (typeof slot === 'number') return { start_slot: slot, end_slot: slot };
  return { start_slot: slot.start, end_slot: slot.end };
};

/** Convert VB's ParsedSlot to API type LocationInput for a single slot or LocationRangeInput for multiple slots */
export const parsedSlotToLocation = (
  startLocation: string,
  slot: ParsedSlot
): LocationInput | LocationRangeInput => {
  if (typeof slot === 'number') {
    return {
      instrument: startLocation,
      slot,
    };
  } else {
    return {
      instrument: startLocation,
      start_slot: slot.start,
      end_slot: slot.end,
    };
  }
};

export const getLocation = (
  startLocation: string,
  slots: ParsedSlot[]
): LabwareItemInput['location'] => {
  const firstItem = slots[0];
  const isSingleLocation = slots.length === 1 && firstItem !== undefined;

  if (isSingleLocation) return parsedSlotToLocation(startLocation, firstItem);

  return slots.map((slot) => parsedSlotToLocation(startLocation, slot));
};

export const locationToParsedSlots = (
  location: LabwareItemInput['location']
): ParsedSlot[] => {
  if (Array.isArray(location)) {
    return location.map((loc) => {
      if ('slot' in loc) return loc.slot;
      return { start: loc.start_slot, end: loc.end_slot };
    });
  } else {
    if ('slot' in location) return [location.slot];
    return [{ start: location.start_slot, end: location.end_slot }];
  }
};

/** Convert SlotFillingRule to slot range string */
export const slotFillingRuleToString = (
  rule: SlotFillingRule
): string | null => {
  const ranges = Array.isArray(rule.range) ? rule.range : [rule.range];
  const parsedSlots = ranges.map<ParsedSlot>((range) => ({
    start: range.start_slot,
    end: range.end_slot,
  }));

  return stringifyParsedSlots(parsedSlots);
};
